import React from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import Stack from '@mui/material/Stack';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideContractPastePage: React.FC = () => {
  return (
    <GuidePageLayout
      title="ペースト定義管理・スプレッドシートコピー"
      description="契約・契約申請の情報をGoogleスプレッドシートに貼り付けるためのカラム構成を定義し、ワンクリックでTSV形式のデータをクリップボードにコピーします。"
      breadcrumbs={[{ label: '契約管理', path: '/guide/contract/overview' }]}
    >
      {/* アクセス権限 */}
      <GuideSection title="アクセス権限">
        <GuideTable
          headers={['機能', 'ADMIN', 'BACK_OFFICE', 'MANAGER']}
          rows={[
            ['ペースト定義管理（登録・編集・削除）', '○', '○', '×'],
            ['ペースト定義一覧取得', '○', '○', '○'],
            ['コピー操作（契約詳細画面）', '○', '○', '○'],
            ['コピー操作（契約申請詳細画面）', '○', '○', '○'],
          ]}
        />
      </GuideSection>

      {/* ペースト定義管理画面 */}
      <GuideSection title="ペースト定義管理画面">
        <GuideStepList
          steps={[
            {
              label: 'サイドバーの「ペースト定義」メニューを選択する',
              description: '契約管理ドメインの設定セクションにあります。',
              content: (
                <GuideMockContainer label="ナビゲーション">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ pointerEvents: 'none' }}
                  >
                    <ContentPasteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>ペースト定義</span>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '画面構成',
              description:
                '左パネルにペースト定義一覧、右パネルに選択した定義の編集フォームが表示されます。',
            },
          ]}
        />
      </GuideSection>

      {/* 新規登録 */}
      <GuideSection title="ペースト定義を登録する">
        <GuideStepList
          steps={[
            { label: '「新規作成」ボタンをクリックする' },
            {
              label: 'ペースト名を入力する',
              description: '必須。200文字以内。既存のペースト定義と重複不可。',
            },
            {
              label: '左リスト（候補カラム）から右リストにカラムを追加する',
              description:
                '「→」ボタンで末尾に追加、またはドラッグ＆ドロップで任意の位置に挿入できます。左リスト上部のテキストフィールドでカラム名を絞り込み検索できます。',
            },
            {
              label: '右リスト内でドラッグ＆ドロップして並び順を設定する',
              description: '上から順がスプレッドシートの列順（左から右）になります。',
            },
            { label: '「登録」ボタンをクリックする' },
          ]}
        />
        <GuideTable
          headers={['入力項目', '必須', '制約']}
          rows={[
            ['ペースト名', '○', '最大200文字、重複不可'],
            ['カラム一覧', '○', '1件以上選択必須'],
            ['カラム並び順', '○', '右リストの上から順がスプレッドシートの列順'],
          ]}
        />
      </GuideSection>

      {/* 編集 */}
      <GuideSection title="ペースト定義を編集する">
        <GuideStepList
          steps={[
            { label: '左パネルのリストから編集したいペースト定義を選択する' },
            { label: 'ペースト名やカラム構成を変更する' },
            { label: '「更新」ボタンをクリックする' },
          ]}
        />
        <GuideInfoBox>
          編集時のカラム一覧は洗い替え（既存を全削除→新規登録）で更新されます。
        </GuideInfoBox>
      </GuideSection>

      {/* 削除 */}
      <GuideSection title="ペースト定義を削除する">
        <GuideStepList
          steps={[
            { label: '左パネルのリストから削除したいペースト定義を選択する' },
            { label: '「削除」ボタンをクリックする' },
            { label: '確認ダイアログで「削除する」をクリックする' },
          ]}
        />
        <GuideInfoBox type="warning">
          削除は不可逆操作です。一度削除すると復元できません。
        </GuideInfoBox>
      </GuideSection>

      {/* コピー操作 */}
      <GuideSection title="契約情報をスプレッドシート用にコピーする">
        <GuideStepList
          steps={[
            {
              label: '契約詳細画面（または契約申請詳細画面）を開く',
            },
            {
              label: 'Split Buttonの▼からペースト定義を選択する',
              description: '初期値は一覧の1件目が自動選択されます。',
              content: (
                <GuideMockContainer label="Split Button">
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      pointerEvents: 'none',
                      border: 1,
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#1976d2',
                        padding: '4px 12px',
                      }}
                    >
                      請求書用
                    </span>
                    <span
                      style={{
                        borderLeft: '1px solid #1976d2',
                        padding: '4px 4px',
                        display: 'flex',
                      }}
                    >
                      <ArrowDropDownIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </span>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: 'Split Buttonの本体（ペースト定義名の部分）をクリックする',
              description: 'プレビューダイアログが開きます。',
            },
            {
              label: 'プレビューテーブルで値を確認する',
              description:
                'ヘッダー行にカラムの表示名（APIから取得）、データ行に値が表示されます。セルをクリックするとインライン編集できます。',
            },
            {
              label: 'コピーアイコンをクリックする',
              description: '値の行がTSV（タブ区切り）でクリップボードにコピーされます。',
              content: (
                <GuideMockContainer label="コピーアイコン">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ pointerEvents: 'none' }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>
                      クリップボードにコピー
                    </span>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: 'ダイアログを閉じてGoogleスプレッドシートに貼り付ける',
              description: 'Ctrl+V（Mac: Cmd+V）で各セルに値が入ります。',
            },
          ]}
        />
      </GuideSection>

      {/* 値解決ルール */}
      <GuideSection title="出力値の変換ルール">
        <GuideTable
          headers={['項目種別', '変換ルール']}
          rows={[
            ['都道府県・契約形態・契約単位', 'コード値 → 表示名に自動変換'],
            ['ステータス（契約）', 'ACTIVE → 契約中、TERMINATED → 契約終了'],
            ['申請種別', 'CREATE → 新規申請、CHANGE → 変更申請、TERMINATE → 解除申請'],
            [
              '申請ステータス',
              'ADJUSTING → 調整中、SUBMITTED → 申請中、IN_PROGRESS → 対応中、APPROVED → 承認、REJECTED → 否決、CANCELLED → 取消',
            ],
            ['boolean項目', 'true → 〇、false → ×'],
            ['複数値（配列）', 'カンマ区切りで1セルにまとめる'],
            ['nullの場合', '空文字として出力'],
            ['日時', 'APIレスポンスの形式そのまま（yyyy-MM-ddTHH:mm:ss）'],
            ['タブ文字を含む値', '半角スペースに置換'],
          ]}
        />
      </GuideSection>

      {/* 出力形式 */}
      <GuideSection title="出力形式">
        <GuideTable
          headers={['項目', '仕様']}
          rows={[
            ['区切り文字', 'タブ（TSV形式）'],
            ['出力行数', '値のみ1行（ヘッダー行なし）'],
            ['プレビューのヘッダー', 'ペースト定義一覧取得APIが返す表示名（displayName）を使用'],
            ['貼り付け先', 'Googleスプレッドシートの任意のセルを選択してペースト'],
          ]}
        />
      </GuideSection>

      {/* ボタンと操作 */}
      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '表示条件', '活性条件', '動作']}
          rows={[
            [
              'Split Button',
              '契約詳細/契約申請詳細の閲覧モード',
              'ペースト定義が1件以上登録済み',
              'プレビューダイアログを開く',
            ],
            [
              '▼（ドロップダウン）',
              'Split Buttonが表示中',
              '常に活性',
              'ペースト定義一覧から選択を切り替える',
            ],
            [
              'コピーアイコン',
              'プレビューダイアログ内',
              '常に活性',
              '値行をTSVでクリップボードにコピー',
            ],
            [
              '新規作成（定義管理画面）',
              'ペースト定義管理画面',
              '常に活性',
              '新規登録フォームを表示',
            ],
            [
              '登録/更新',
              '編集フォーム表示中',
              'ペースト名入力済み + カラム1件以上選択',
              'ペースト定義を保存',
            ],
            ['削除', '既存定義選択中', '常に活性', '確認ダイアログ後に物理削除'],
          ]}
        />
      </GuideSection>

      {/* 注意事項 */}
      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            ペースト定義は契約・契約申請の両方で共用します。契約にのみ存在するカラム（ステータス等）を契約申請で使用した場合、該当カラムは空で出力されます。逆も同様です。
          </GuideInfoBox>
          <GuideInfoBox>
            プレビューダイアログ内でセルの値をインライン編集できます。編集した値はクリップボードコピーに反映されますが、元データは変更されません。
          </GuideInfoBox>
          <GuideInfoBox>
            コピー後もダイアログは開いたままです。必要に応じて×ボタンで閉じてください。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            ペースト定義が0件の場合、コピーボタンは表示されません。先にペースト定義管理画面で定義を1件以上登録してください。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            同じペースト名は登録できません。重複する場合はエラーが表示されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideContractPastePage;
